import { Auth } from '@cadstrom/cadstrom-shared';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useCallback, useState } from 'react';

import { abilityOverrideAtom } from '#src/state/atoms/ability-override-atom.js';
import { useTRPC } from '#src/trpc.js';

import type { SwitchProps } from '@mui/material/Switch';

/**
 * This component allows staff to see the UI as a regular user would see it. When switched on, it will query for the
 * current user with the `viewAsRole` parameter set to `Member`, which will return the permissions of a regular user.
 * We then set the global `abilityOverride` atom to this new ability, which is picked up by the `AbilityContextProvider`
 * and used to override the ability object used for authorization checks throughout the app.
 */
export function ViewAsUserToggle() {
	const trpc = useTRPC();

	const [abilityOverride, setAbilityOverride] = useAtom(abilityOverrideAtom);
	const [optimisticSwitchPosition, setOptimisticSwitchPosition] = useState<boolean | undefined>(undefined);

	const { refetch, isLoading } = useQuery(
		trpc.user.getCurrent.queryOptions(
			{ viewAsRole: Auth.MembershipRole.Member },
			{
				enabled: false,
				staleTime: Infinity,
			},
		),
	);

	const handleChange: NonNullable<SwitchProps['onChange']> = useCallback(
		(event) => {
			if (event.target.checked) {
				setOptimisticSwitchPosition(true);
				void refetch()
					.then(({ data }) => {
						if (data?.auth.rules) {
							setAbilityOverride({ rules: data.auth.rules });
						}
					})
					.finally(() => setOptimisticSwitchPosition(undefined));
			} else {
				setAbilityOverride({ rules: null });
				setOptimisticSwitchPosition(undefined);
			}
		},
		[refetch, setAbilityOverride],
	);

	return (
		<FormControlLabel
			control={
				<Switch
					checked={optimisticSwitchPosition ?? abilityOverride.rules !== null}
					disabled={isLoading}
					onChange={handleChange}
				/>
			}
			label='View as user'
			className='grow'
		/>
	);
}
